import React, { useContext, useLayoutEffect } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import NoMatch from './composed/nomatch/NoMatch';
import {
  getOnLoadRegBranchPayloadFromStorage,
  getStoredAccessToken,
} from './utils/regBranchOnLoadPayload';
import {
  extractDefaultManagingBranchName,
  readRegBranchOnLoadCache,
  upsertDefaultManagingBranchOnCacheBlob,
} from './utils/regBranchOnLoadCache';
import { canAccessRoute, needsTeamPhoneVerification, normalizeRoutePath } from './utils/regBranchRouteAccess';
import { GlobalStateDataContext } from './context-api/data-provider/GlobalStateDataProvider';

import Dashboard from './pages/DashBoard';
import PageBase from './composed/pagebase/Pagebase';
import ConnectedUsers from './pages/ConnectedUsers/ConnectedUsers';
import Header from './composed/header/Header';
import StoreManagement from './pages/StoreManagement/StoreManagement';
import StoreDetailsPage from './pages/StoreManagement/store-details/StoreDetailsPage';
import OrderManagement from './pages/OrderManagement/OrderManagement';
import OrderManagementMap from './pages/OrderManagement/OrderManagementMap';
import Squads from './pages/Squads/Squads';
import StoreLeads from './pages/StoreLeads/StoreLeads';
import ChicagoZipMap from './pages/ZipcodeManaging/ZipcodeMap';
import AiAgentLogo from './pages/AiAgentLogo/AiAgentLogo';
import MenuUploadPage from './pages/menu-upload-page/MenuUpload-Page';
import MenuUploadFormPage from './pages/menu-upload-page/MenuUploadForm-Page';
import KitchenProducts from './pages/Products/KitchenProducts';
import AddKitchenProduct from './pages/Products/AddKitchenProduct';
import MarketingLead from './pages/MarketingLead/MarketingLead';
import AdLibrary from './pages/AdLibrary/AdLibrary';
import BranchTeams from './pages/BranchTeams/BranchTeams';
import ZipcodeAnalytics from './pages/ZipcodeAnalytics/ZipcodeAnalytics';
import ServerMonitoring from './pages/ServerMonitoring/ServerMonitoring';
import TwilioPhone from './pages/TwilioPhone/TwilioPhone';
import Notifications from './pages/Notifications/Notifications';
import PhoneVerifyOtp from './pages/phoneverify-page/PhoneVerifyOtp-Page';
import Login from './pages/Login/Login';
import BranchResetPassword from './pages/Login/BranchResetPassword';

/**
 * When the user has a JWT (not on /login), POST /OnLoadRegBranchInformation once per token.
 * JWT is attached on every request via axios requestInterceptor (see context-api/axios).
 */
function RegBranchBootstrap() {
  const location = useLocation();
  const {
    OnLoadRegBranchInformation,
    setOnLoadRegBranchResponse,
    lastOnLoadRegBranchTokenRef,
    refreshRegBranchOnLoadInBackground,
  } = useContext(GlobalStateDataContext);

  /**
   * Await encrypted cache (or network) before paint so permissions never flash wrong.
   * Cache hit: hydrate from localStorage, then refresh OnLoad in background.
   */
  useLayoutEffect(() => {
    if (location.pathname === '/login' || location.pathname === '/reset-password') return;
    let cancelled = false;
    (async () => {
      const token = getStoredAccessToken();
      if (!token) {
        lastOnLoadRegBranchTokenRef.current = null;
        return;
      }
      const payload = getOnLoadRegBranchPayloadFromStorage();
      if (!payload) return;

      if (lastOnLoadRegBranchTokenRef.current === token) {
        return;
      }

      let cached = null;
      try {
        cached = await readRegBranchOnLoadCache(token);
      } catch {
        cached = null;
      }
      if (cancelled) return;

      const cachedOk =
        cached &&
        cached.success === true &&
        cached.data != null &&
        typeof cached.data === 'object';

      if (cachedOk) {
        const managingBranch = extractDefaultManagingBranchName(cached);
        if (managingBranch) {
          upsertDefaultManagingBranchOnCacheBlob(managingBranch);
        }
        if (cancelled) return;
        setOnLoadRegBranchResponse({
          loading: false,
          success: true,
          message: cached.message ?? null,
          data: { data: cached },
        });
        lastOnLoadRegBranchTokenRef.current = token;
        void refreshRegBranchOnLoadInBackground(payload);
        return;
      }

      if (cancelled) return;
      await OnLoadRegBranchInformation(payload);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    location.pathname,
    OnLoadRegBranchInformation,
    setOnLoadRegBranchResponse,
    lastOnLoadRegBranchTokenRef,
    refreshRegBranchOnLoadInBackground,
  ]);

  return null;
}


/**
 * Guards all non-/login routes. If there's no stored JWT we send the user
 * to /login and remember where they tried to go (state.from) so we can
 * (optionally) return them there after a successful login.
 */
function ProtectedRoute({ children }) {
  const location = useLocation();
  const token = getStoredAccessToken();

  if (!token) {
    return <Navigate to='/login' replace state={{ from: location }} />;
  }
  return children;
}

/**
 * Inverse guard for /login. If the user is already authenticated and lands
 * on /login (e.g. via back button or pasted URL), bounce them to the app.
 */
function PublicOnlyRoute({ children }) {
  const token = getStoredAccessToken();
  if (token) {
    return <Navigate to='/' replace />;
  }
  return children;
}

/**
 * After a successful OnLoadRegBranchInformation, team members (session has TeamInfo + TeamRoleAccess)
 * may only open paths allowed by routeAccess; anything else redirects to the dashboard.
 */
function RouteAccessGuard({ children }) {
  const location = useLocation();
  const { regBranchSession, onLoadRegBranchResponse } = useContext(GlobalStateDataContext);

  const sessionSettled =
    onLoadRegBranchResponse.loading === false && onLoadRegBranchResponse.success === true;

  if (sessionSettled && !canAccessRoute(location.pathname, regBranchSession)) {
    return <Navigate to='/' replace />;
  }

  return children;
}

/** Team users with unverified phone may only use `/phone-verify` until `TeamInfo.phone_verified_status` is true. */
function TeamPhoneVerificationGate({ children }) {
  const location = useLocation();
  const { regBranchSession } = useContext(GlobalStateDataContext);
  if (!needsTeamPhoneVerification(regBranchSession)) return children;
  if (normalizeRoutePath(location.pathname) === '/phone-verify') return children;
  return <Navigate to='/phone-verify' replace />;
}

/**
 * Blocks Header, sidebar, and routes until OnLoadRegBranchInformation succeeds so
 * team members never see unrestricted tiles or mount protected pages for a frame.
 */
function RegBranchHydrationGate({ children }) {
  const navigate = useNavigate();
  const { onLoadRegBranchResponse, retryOnLoadRegBranchInformation } = useContext(
    GlobalStateDataContext
  );

  const token = getStoredAccessToken();
  const payload = getOnLoadRegBranchPayloadFromStorage();
  const { success, message } = onLoadRegBranchResponse;

  const shellStyle = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#181436',
    color: '#E3EEB2',
    fontFamily: 'Inter, system-ui, sans-serif',
    padding: '24px',
    textAlign: 'center',
    boxSizing: 'border-box',
  };

  const btnStyle = {
    marginTop: '20px',
    padding: '10px 20px',
    cursor: 'pointer',
    borderRadius: '5px',
    border: '1px solid #E3EEB2',
    backgroundColor: '#024CAA',
    color: 'white',
    fontFamily: 'inherit',
    fontSize: '15px',
  };

  if (token && !payload) {
    return (
      <div style={shellStyle}>
        <div style={{ maxWidth: '420px', lineHeight: 1.5 }}>
          Branch session could not be loaded from this browser (missing branch or user id). Please sign
          in again.
        </div>
        <button
          type='button'
          style={btnStyle}
          onClick={() => navigate('/login', { replace: true })}
        >
          Sign in
        </button>
      </div>
    );
  }

  if (success === true) {
    return children;
  }

  if (success === false) {
    return (
      <div style={shellStyle}>
        <div style={{ maxWidth: '480px', lineHeight: 1.5, color: '#ffb4b4' }}>
          {typeof message === 'string' && message.trim() ? message.trim() : 'Could not load branch permissions.'}
        </div>
        <button type='button' style={btnStyle} onClick={() => retryOnLoadRegBranchInformation()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={shellStyle}>
      <div
        style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(227, 238, 178, 0.25)',
          borderTopColor: '#E3EEB2',
          borderRadius: '50%',
          animation: 'regBranchSpin 0.75s linear infinite',
        }}
      />
      <style>{`@keyframes regBranchSpin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ marginTop: '20px', fontSize: '16px', color: 'white' }}>Loading branch permissions…</div>
    </div>
  );
}

/* Main app layout — Header + page content */
function MainLayout() {
  return (
    <div className='app-main-layout'>
      <RegBranchBootstrap />
      <RegBranchHydrationGate>
        <TeamPhoneVerificationGate>
          <Header />
          <PageBase>
            <RouteAccessGuard>
              <Routes>
              <Route path='/' element={<Dashboard />} />
              <Route path='/products' element={<KitchenProducts />} />
              <Route path='/products/add' element={<AddKitchenProduct />} />
              <Route path='/connectedusers' element={<ConnectedUsers />} />
              <Route path='/storemanagement' element={<StoreManagement />} />
              <Route path='/storemanagement/:storeId' element={<StoreDetailsPage />} />
              <Route path='/ordermanagement' element={<OrderManagement />} />
              <Route path='/ordermanagement/map' element={<OrderManagementMap />} />
              <Route path='/squads' element={<Squads />} />
              <Route path='/teams' element={<BranchTeams />} />
              <Route path='/analytics' element={<ZipcodeAnalytics />} />
              <Route path='/servermonitoring' element={<ServerMonitoring />} />
              <Route path='/notifications' element={<Notifications />} />
              <Route path='/storeleads' element={<StoreLeads />} />
              <Route path='/marketinglead' element={<MarketingLead />} />
              <Route path='/adlibrary' element={<AdLibrary />} />
              <Route path='/zipcodemap' element={<ChicagoZipMap />} />
              <Route path='/productagent' element={<MenuUploadFormPage />} />
              <Route path='/logoagent' element={<AiAgentLogo />} />
              <Route path='/menuuploadjobs' element={<MenuUploadPage />} />
              <Route path='/twiliophone' element={<TwilioPhone />} />
              <Route path='/phone-verify' element={<PhoneVerifyOtp />} />
              <Route path='*' element={<NoMatch textmessage='No route found' />} />
              </Routes>
            </RouteAccessGuard>
          </PageBase>
        </TeamPhoneVerificationGate>
      </RegBranchHydrationGate>
    </div>
  );
}

function App() {
  return (
    <Routes>
      {/* Login renders full-screen without Header / PageBase */}
      <Route
        path='/login'
        element={
          <PublicOnlyRoute>
            <Login />
          </PublicOnlyRoute>
        }
      />

      <Route
        path='/reset-password'
        element={<BranchResetPassword />}
      />

      {/* Everything else requires a valid session */}
      <Route
        path='/*'
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default App;
